import React, { useState, useEffect, useMemo } from 'react';
import Fuse from 'fuse.js';
import { referenceSheetManager } from '../../services/reference-sheet-manager';
import type { ReferenceEntry } from '../../types';

type BulkEntry = {
  id: number;
  reference: string;
  tag: string;
  originalTag: string;
};

type BulkResultStatus = 'pending' | 'success' | 'error';

export const ReferencesTab: React.FC = () => {
  const [references, setReferences] = useState<ReferenceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newReferenceText, setNewReferenceText] = useState('');
  const [customTag, setCustomTag] = useState('');
  const [addingReference, setAddingReference] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStep, setBulkStep] = useState<'input' | 'review'>('input');
  const [bulkInput, setBulkInput] = useState('');
  const [bulkInputError, setBulkInputError] = useState<string | null>(null);
  const [bulkEntries, setBulkEntries] = useState<BulkEntry[]>([]);
  const [bulkResults, setBulkResults] = useState<Record<number, { status: BulkResultStatus; message?: string }>>({});
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [deleteConfirmTag, setDeleteConfirmTag] = useState<string | null>(null);
  const [deletingTag, setDeletingTag] = useState<string | null>(null);

  useEffect(() => {
    loadReferences();
  }, []);

  const loadReferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const refs = await referenceSheetManager.getAllReferences(true);
      setReferences(refs);
    } catch (err) {
      console.error('Failed to load references:', err);
      setError(err instanceof Error ? err.message : 'Failed to load references');
    } finally {
      setLoading(false);
    }
  };

  const fuse = useMemo(() => {
    return new Fuse(references, {
      keys: [
        { name: 'tag', weight: 2 },
        { name: 'reference', weight: 1 }
      ],
      threshold: 0.3,
      includeScore: true,
      minMatchCharLength: 2
    });
  }, [references]);

  const filteredReferences = useMemo(() => {
    if (!searchTerm.trim()) {
      return references;
    }

    const results = fuse.search(searchTerm);
    return results.map(result => result.item);
  }, [references, searchTerm, fuse]);

  const handleAddReference = async () => {
    if (!newReferenceText.trim()) {
      setAddError('Please enter a reference');
      return;
    }

    try {
      setAddingReference(true);
      setAddError(null);

      const result = await referenceSheetManager.addReference(
        newReferenceText.trim(),
        customTag.trim() || undefined
      );

      if (result.success) {
        await loadReferences();
        setShowAddModal(false);
        setNewReferenceText('');
        setCustomTag('');
      } else {
        setAddError(result.error || 'Failed to add reference');
      }
    } catch (err) {
      console.error('Error adding reference:', err);
      setAddError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setAddingReference(false);
    }
  };

  const handleGenerateTag = () => {
    if (newReferenceText.trim()) {
      const generatedTag = referenceSheetManager.generateReferenceTag(newReferenceText.trim());
      setCustomTag(generatedTag);
    }
  };

  const handleDeleteReference = async (tag: string) => {
    try {
      setDeletingTag(tag);
      const result = await referenceSheetManager.deleteReference(tag);

      if (result.success) {
        await loadReferences();
        setDeleteConfirmTag(null);
      } else {
        setError(result.error || 'Failed to delete reference');
      }
    } catch (err) {
      console.error('Error deleting reference:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setDeletingTag(null);
    }
  };

  const resetBulkState = () => {
    setBulkStep('input');
    setBulkInput('');
    setBulkInputError(null);
    setBulkEntries([]);
    setBulkResults({});
    setIsBulkImporting(false);
  };

  const handleOpenBulkModal = () => {
    resetBulkState();
    setShowBulkModal(true);
  };

  const handleCloseBulkModal = () => {
    resetBulkState();
    setShowBulkModal(false);
  };

  const handleBulkNext = () => {
    const lines = bulkInput
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (lines.length === 0) {
      setBulkInputError('Please paste at least one reference. Separate references with new lines.');
      return;
    }

    const entries: BulkEntry[] = lines.map((reference, index) => {
      const tag = referenceSheetManager.generateReferenceTag(reference);
      return {
        id: index,
        reference,
        tag,
        originalTag: tag
      };
    });

    setBulkEntries(entries);
    setBulkResults({});
    setBulkStep('review');
  };

  const handleBulkEntryUpdate = (id: number, field: 'reference' | 'tag', value: string) => {
    setBulkEntries(prev =>
      prev.map(entry =>
        entry.id === id
          ? {
              ...entry,
              [field]: field === 'tag' ? value : value
            }
          : entry
      )
    );
    setBulkResults(prev => {
      if (!(id in prev)) return prev;
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleBulkRegenerateTag = (id: number) => {
    setBulkEntries(prev =>
      prev.map(entry =>
        entry.id === id
          ? {
              ...entry,
              tag: referenceSheetManager.generateReferenceTag(entry.reference)
            }
          : entry
      )
    );
    setBulkResults(prev => {
      if (!(id in prev)) return prev;
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleBulkRemove = (id: number) => {
    setBulkEntries(prev => prev.filter(entry => entry.id !== id));
    setBulkResults(prev => {
      if (!(id in prev)) return prev;
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleBulkBackToInput = () => {
    setBulkInput(bulkEntries.map(entry => entry.reference).join('\n'));
    setBulkStep('input');
    setBulkResults({});
    setIsBulkImporting(false);
  };

  const handleBulkImport = async () => {
    if (bulkEntries.length === 0) {
      setBulkInputError('No references to import.');
      setBulkStep('input');
      return;
    }

    setIsBulkImporting(true);
    setBulkResults({});

    const results: Record<number, { status: BulkResultStatus; message?: string }> = {};
    let successfulImports = 0;

    for (const entry of bulkEntries) {
      try {
        const result = await referenceSheetManager.addReference(
          entry.reference.trim(),
          entry.tag.trim() || undefined
        );

        if (result.success) {
          results[entry.id] = { status: 'success' };
          successfulImports += 1;
        } else {
          results[entry.id] = {
            status: 'error',
            message: result.error || 'Failed to import reference'
          };
        }
      } catch (importError) {
        const message = importError instanceof Error ? importError.message : 'Unknown error';
        results[entry.id] = { status: 'error', message };
      }

      setBulkResults(curr => ({ ...curr, [entry.id]: results[entry.id] }));
    }

    if (successfulImports > 0) {
      await loadReferences();
    }

    setIsBulkImporting(false);
  };

  const bulkSuccessCount = useMemo(
    () => Object.values(bulkResults).filter(result => result.status === 'success').length,
    [bulkResults]
  );

  const bulkErrorCount = useMemo(
    () => Object.values(bulkResults).filter(result => result.status === 'error').length,
    [bulkResults]
  );

  return (
    <div className="flex flex-col h-full bg-white text-gray-900">
      <div className="flex items-center gap-4 px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex-1 relative group">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-gray-400 group-focus-within:text-unity-blue transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search references by tag or text..."
            className="w-full pl-12 pr-10 py-3 bg-white rounded-xl border border-gray-300 focus:outline-none focus:border-unity-blue focus:ring-2 focus:ring-unity-blue/20 text-gray-900 placeholder-gray-500 transition-all duration-200 shadow-sm"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="px-6 py-3 bg-gradient-to-r from-[#5aa8ff] to-[#3f8dff] hover:from-[#3f8dff] hover:to-[#2d76ff] rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
          title="Add new reference"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Add Reference</span>
        </button>
        <button
          onClick={handleOpenBulkModal}
          className="px-5 py-3 bg-white border border-gray-300 text-gray-700 hover:border-unity-blue hover:text-unity-blue rounded-xl font-semibold transition-all duration-200 shadow-sm hover:shadow flex items-center gap-2"
          title="Bulk import references"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v7m0 0l3-3m-3 3l-3-3m-6 3a9 9 0 1118 0 9 9 0 01-18 0z" />
          </svg>
          <span>Bulk Import</span>
        </button>
      </div>

      <div className="flex-1 overflow-auto px-6 py-5 bg-gray-50">
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-unity-blue mb-4" />
            <div className="text-gray-600 text-lg">Loading references...</div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700 shadow-sm">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <strong className="font-semibold">Error:</strong>
                <p className="mt-1 text-sm leading-6">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && references.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <svg className="w-20 h-20 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <div className="text-gray-700 text-lg mb-2">No references yet</div>
            <div className="text-gray-500 text-sm">Click "Add Reference" to create your first reference</div>
          </div>
        )}

        {!loading && !error && filteredReferences.length === 0 && references.length > 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <div className="text-gray-700 text-lg">No references match "{searchTerm}"</div>
            <div className="text-gray-500 text-sm mt-1">Try a different search term</div>
          </div>
        )}

        {!loading && !error && filteredReferences.length > 0 && (
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
            <div className="grid grid-cols-[240px_1fr_80px] bg-gray-50 font-semibold text-sm border-b border-gray-200">
              <div className="px-6 py-4 border-r border-gray-200 flex items-center gap-2">
                <svg className="w-4 h-4 text-unity-blue" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V6.414A2 2 0 0016.414 5L14 2.586A2 2 0 0012.586 2H9z" />
                  <path d="M3 8a2 2 0 012-2v10h8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                </svg>
                <span className="text-gray-700">Reference Tag</span>
              </div>
              <div className="px-6 py-4 border-r border-gray-200 flex items-center gap-2">
                <svg className="w-4 h-4 text-unity-blue" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">Reference</span>
              </div>
              <div className="px-4 py-4 text-center">
                <span className="text-gray-700">Actions</span>
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {filteredReferences.map((ref, index) => (
                <div
                  key={index}
                  className="grid grid-cols-[240px_1fr_80px] hover:bg-blue-50 transition-all duration-150 group"
                >
                  <div className="px-6 py-4 border-r border-gray-100 font-mono text-unity-blue text-sm font-semibold group-hover:text-unity-blue-dark transition-colors">
                    {ref.tag}
                  </div>
                  <div className="px-6 py-4 border-r border-gray-100 text-gray-700 overflow-x-auto whitespace-nowrap text-sm leading-relaxed hover:cursor-text">
                    {ref.reference}
                  </div>
                  <div className="px-4 py-4 flex items-center justify-center">
                    <button
                      onClick={() => setDeleteConfirmTag(ref.tag)}
                      disabled={deletingTag === ref.tag}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete reference"
                    >
                      {deletingTag === ref.tag ? (
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && !error && references.length > 0 && (
          <div className="mt-6 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span>Showing <strong className="text-gray-700">{filteredReferences.length}</strong> of <strong className="text-gray-700">{references.length}</strong> references</span>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl border border-gray-200 max-w-3xl w-full max-h-[90vh] overflow-auto shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-unity-blue/10 rounded-lg">
                  <svg className="w-6 h-6 text-unity-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Add New Reference</h2>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewReferenceText('');
                  setCustomTag('');
                  setAddError(null);
                }}
                className="text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-8 py-6 space-y-6 bg-white">
              <div>
                <label className="block text-sm font-semibold mb-3 text-gray-700 flex items-center gap-2">
                  <svg className="w-4 h-4 text-unity-blue" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                  Reference Text
                </label>
                <textarea
                  value={newReferenceText}
                  onChange={(e) => setNewReferenceText(e.target.value)}
                  placeholder="Paste or type the full reference here (e.g., Smith, J. (2025). Title of work...)"
                  className="w-full h-36 px-4 py-3 bg-white rounded-xl border border-gray-300 focus:outline-none focus:border-unity-blue focus:ring-2 focus:ring-unity-blue/20 text-gray-900 placeholder-gray-500 resize-none transition-all duration-200 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-3 text-gray-700 flex items-center gap-2">
                  <svg className="w-4 h-4 text-unity-blue" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  Reference Tag (optional)
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={customTag}
                    onChange={(e) => setCustomTag(e.target.value)}
                    placeholder="[Author Year] - Leave empty to auto-generate"
                    className="flex-1 px-4 py-3 bg-white rounded-xl border border-gray-300 focus:outline-none focus:border-unity-blue focus:ring-2 focus:ring-unity-blue/20 text-gray-900 placeholder-gray-500 transition-all duration-200 shadow-sm font-mono"
                  />
                  <button
                    onClick={handleGenerateTag}
                    disabled={!newReferenceText.trim()}
                    className="px-6 py-3 bg-gray-100 border border-gray-300 rounded-xl transition-all duration-200 font-medium whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-200 enabled:hover:border-unity-blue enabled:hover:bg-unity-blue/10"
                  >
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Generate
                    </span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Click "Generate" to auto-extract tag from reference text
                </p>
              </div>

              {addError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm leading-6">{addError}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewReferenceText('');
                  setCustomTag('');
                  setAddError(null);
                }}
                className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddReference}
                disabled={addingReference || !newReferenceText.trim()}
                className="px-6 py-2.5 bg-gradient-to-r from-[#5aa8ff] to-[#3f8dff] hover:from-[#3f8dff] hover:to-[#2d76ff] rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-md hover:shadow-lg disabled:shadow-none flex items-center gap-2"
              >
                {addingReference ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Adding...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Reference
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl border border-gray-200 max-w-5xl w-full max-h-[92vh] overflow-auto shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-[#5aa8ff] to-[#3f8dff] text-white rounded-lg">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Bulk Import References</h2>
                    <p className="text-sm text-gray-600">Step {bulkStep === 'input' ? '1' : '2'} of 2</p>
                  </div>
                </div>
              </div>
              <button
                onClick={handleCloseBulkModal}
                className="text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-8 py-6 space-y-6">
              {bulkStep === 'input' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
                    Paste each reference on its own line. Blank lines will be ignored.
                  </div>
                  <textarea
                    value={bulkInput}
                    onChange={(e) => {
                      setBulkInput(e.target.value);
                      if (bulkInputError) setBulkInputError(null);
                    }}
                    placeholder={`Simon, G. (2025). Macbooks are the best...\nDoe, A.; Rey, B. (2023). Localization Patterns...`}
                    className="w-full h-64 px-4 py-3 bg-white rounded-xl border border-gray-300 focus:outline-none focus:border-unity-blue focus:ring-2 focus:ring-unity-blue/20 text-gray-900 placeholder-gray-500 resize-none transition-all duration-200 shadow-sm"
                  />
                  {bulkInputError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                      {bulkInputError}
                    </div>
                  )}
                </div>
              )}

              {bulkStep === 'review' && (
                <div className="space-y-4">
                  {bulkEntries.length === 0 ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
                      All entries were removed. Go back to add references to import.
                    </div>
                  ) : (
                    <div className="rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                      <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)_auto] bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <div className="px-4 py-3">Reference</div>
                        <div className="px-4 py-3">Tag</div>
                        <div className="px-4 py-3 text-center">Actions</div>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {bulkEntries.map(entry => {
                          const result = bulkResults[entry.id];
                          return (
                            <div key={entry.id} className="grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)_auto] items-stretch">
                              <div className="p-4 border-r border-gray-100">
                                <textarea
                                  value={entry.reference}
                                  onChange={(e) => handleBulkEntryUpdate(entry.id, 'reference', e.target.value)}
                                  disabled={isBulkImporting}
                                  className="w-full min-h-[96px] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-unity-blue focus:ring-2 focus:ring-unity-blue/20 text-gray-900 resize-y"
                                />
                              </div>
                              <div className="p-4 border-r border-gray-100 flex flex-col gap-3">
                                <input
                                  type="text"
                                  value={entry.tag}
                                  onChange={(e) => handleBulkEntryUpdate(entry.id, 'tag', e.target.value)}
                                  disabled={isBulkImporting}
                                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-unity-blue focus:ring-2 focus:ring-unity-blue/20 text-gray-900 font-mono"
                                />
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                  <button
                                    type="button"
                                    onClick={() => handleBulkRegenerateTag(entry.id)}
                                    disabled={isBulkImporting}
                                    className="text-unity-blue hover:text-unity-blue-dark"
                                  >
                                    Regenerate
                                  </button>
                                  {entry.originalTag !== entry.tag && (
                                    <span className="text-amber-600">Edited</span>
                                  )}
                                  {!entry.tag.trim() && (
                                    <span className="text-red-600">Tag required</span>
                                  )}
                                </div>
                                {result && result.status === 'error' && (
                                  <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                    {result.message}
                                  </div>
                                )}
                                {result && result.status === 'success' && (
                                  <div className="text-xs text-green-600 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
                                    Imported
                                  </div>
                                )}
                              </div>
                              <div className="p-4 flex flex-col items-center justify-between gap-3">
                                <button
                                  type="button"
                                  onClick={() => handleBulkRemove(entry.id)}
                                  disabled={isBulkImporting}
                                  className="px-3 py-2 text-xs border border-gray-300 rounded-lg text-gray-600 hover:text-red-600 hover:border-red-400 transition-colors"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50">
              {bulkStep === 'review' && (
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <div>
                    {bulkEntries.length} reference{bulkEntries.length === 1 ? '' : 's'} ready to import
                  </div>
                  {(bulkSuccessCount > 0 || bulkErrorCount > 0) && (
                    <div className="flex items-center gap-4">
                      {bulkSuccessCount > 0 && (
                        <span className="text-green-600">{bulkSuccessCount} imported</span>
                      )}
                      {bulkErrorCount > 0 && (
                        <span className="text-red-600">{bulkErrorCount} failed</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3">
                {bulkStep === 'review' && (
                  <button
                    onClick={handleBulkBackToInput}
                    disabled={isBulkImporting}
                    className="px-5 py-2.5 bg-white border border-gray-300 hover:border-gray-400 rounded-xl transition-all duration-200 text-sm"
                  >
                    Back
                  </button>
                )}
                <button
                  onClick={handleCloseBulkModal}
                  disabled={isBulkImporting}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 text-sm"
                >
                  Cancel
                </button>
                {bulkStep === 'input' && (
                  <button
                    onClick={handleBulkNext}
                    className="px-5 py-2.5 bg-gradient-to-r from-[#5aa8ff] to-[#3f8dff] hover:from-[#3f8dff] hover:to-[#2d76ff] text-white rounded-xl transition-all duration-200 font-semibold shadow-md hover:shadow-lg text-sm"
                  >
                    Next
                  </button>
                )}
                {bulkStep === 'review' && (
                  <button
                    onClick={handleBulkImport}
                    disabled={isBulkImporting || bulkEntries.length === 0}
                    className="px-6 py-2.5 bg-gradient-to-r from-[#5aa8ff] to-[#3f8dff] hover:from-[#3f8dff] hover:to-[#2d76ff] text-white rounded-xl transition-all duration-200 font-semibold shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isBulkImporting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v7m0 0l3-3m-3 3l-3-3m-6 3a9 9 0 1118 0 9 9 0 01-18 0z" />
                        </svg>
                        Import
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmTag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]" onClick={() => setDeleteConfirmTag(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Delete Reference</h3>
              </div>
            </div>

            <div className="px-6 py-5">
              <p className="text-gray-700 mb-3">
                Are you sure you want to delete this reference?
              </p>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="font-mono text-sm text-unity-blue font-semibold mb-1">
                  {deleteConfirmTag}
                </div>
                <div className="text-sm text-gray-600">
                  {references.find(r => r.tag === deleteConfirmTag)?.reference}
                </div>
              </div>
              <p className="text-sm text-red-600 mt-3">
                This action cannot be undone.
              </p>
            </div>

            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmTag(null)}
                disabled={deletingTag === deleteConfirmTag}
                className="px-5 py-2.5 bg-white border border-gray-300 hover:border-gray-400 rounded-xl transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteReference(deleteConfirmTag)}
                disabled={deletingTag === deleteConfirmTag}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 font-semibold shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deletingTag === deleteConfirmTag ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
